import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, Plus, AlertCircle } from 'lucide-react';
import { expenses as expensesApi, type Expense } from '../../lib/api';
import { STATUS_META } from '../../lib/expenseStatus';

export default function MyExpenses() {
  const [items, setItems] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { expenses } = await expensesApi.mine();
      setItems(expenses);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-6 h-6 text-green-600" /> My Expenses
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Submit and track your expense claims</p>
        </div>
        <Link to="/expenses/new" className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Expense
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="card space-y-3">
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" /></div>
        ) : items.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No expenses submitted yet</p>
        ) : (
          items.map(item => {
            const meta = STATUS_META[item.status];
            return (
              <Link key={item.id} to={`/expenses/${item.id}`} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{item.description || 'Expense'}</p>
                  <p className="text-xs text-gray-500">{item.expense_date}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ${meta.className}`}>{meta.label}</span>
                <p className="font-semibold text-gray-900 tabular-nums flex-shrink-0">{item.amount.toFixed(2)}</p>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
